import { useState } from "react";
import { CheckCircle2, ChevronDown, Search, Loader2 } from "lucide-react";
import type { ToolEvent } from "@/hooks/useChat";

interface ActivityLogProps {
  events: ToolEvent[];
}

export function ActivityLog({ events }: ActivityLogProps) {
  const [expanded, setExpanded] = useState(false);

  if (events.length === 0) {
    return (
      <div className="flex items-center gap-[8px] text-text-muted text-[13px]">
        <Loader2 className="h-[14px] w-[14px] animate-spin shrink-0" aria-hidden />
        <span>Thinking…</span>
      </div>
    );
  }

  const isRunning = events.some((e) => e.status === "running");
  const currentEvent = [...events].reverse().find((e) => e.status === "running");
  const completedEvents = events.filter((e) => e.status !== "running");

  if (isRunning) {
    return (
      <div
        className="rounded-lg border border-[var(--border)] bg-[var(--surface-subtle,var(--surface))] overflow-hidden text-[13px] w-full max-w-[420px]"
        role="status"
        aria-live="polite"
      >
        {/* Animated header */}
        <div className="flex items-center gap-[8px] px-[12px] py-[8px]">
          <Search
            className="h-[13px] w-[13px] text-accent shrink-0 animate-pulse"
            aria-hidden
          />
          <span className="font-medium text-text">Searching…</span>
        </div>

        {/* Current active step */}
        {currentEvent && (
          <div className="border-t border-[var(--border)] px-[12px] py-[6px] bg-[var(--surface)]">
            <span className="text-[12px] text-text-muted leading-snug">
              {currentEvent.label}
            </span>
          </div>
        )}

        {/* Recently completed steps (last 3) */}
        {completedEvents.length > 0 && (
          <div className="border-t border-[var(--border)] px-[12px] py-[6px] flex flex-col gap-[4px]">
            {completedEvents.slice(-3).map((e) => (
              <div key={e.id} className="flex items-center gap-[6px] text-[12px] text-text-muted">
                <CheckCircle2
                  className="h-[11px] w-[11px] text-[color:var(--success,#22c55e)] shrink-0"
                  aria-hidden
                />
                <span className="truncate">
                  {e.label.replace(/…$/, "")}
                  {e.summary ? <span className="ml-1 opacity-70">— {e.summary}</span> : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // All done — collapsible summary card
  const summaryLabel = buildSummaryLabel(events);

  return (
    <div
      className="rounded-lg border border-[var(--border)] bg-[var(--surface-subtle,var(--surface))] overflow-hidden text-[13px] w-full max-w-[420px]"
      role="status"
      aria-live="polite"
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-[8px] px-[12px] py-[8px] text-left hover:bg-[var(--surface)] transition-colors"
        aria-expanded={expanded}
      >
        <CheckCircle2
          className="h-[13px] w-[13px] text-[color:var(--success,#22c55e)] shrink-0"
          aria-hidden
        />
        <span className="flex-1 text-text-secondary font-medium">{summaryLabel}</span>
        <ChevronDown
          className={`h-[13px] w-[13px] text-text-muted transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {expanded && (
        <div className="border-t border-[var(--border)] divide-y divide-[var(--border)]">
          {events.map((e) => (
            <div key={e.id} className="flex items-start gap-[8px] px-[12px] py-[6px]">
              <CheckCircle2
                className="h-[11px] w-[11px] text-[color:var(--success,#22c55e)] mt-[2px] shrink-0"
                aria-hidden
              />
              <span className="text-[12px] leading-snug text-text-muted">
                <span className="text-text-secondary">{e.label.replace(/…$/, "")}</span>
                {e.summary ? (
                  <span className="ml-[6px] opacity-70">— {e.summary}</span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function buildSummaryLabel(events: ToolEvent[]): string {
  const SEARCH_TOOLS = new Set(["search_code", "grep_repo", "search_filenames"]);
  const READ_TOOLS = new Set(["read_file"]);
  const REPO_TOOLS = new Set(["repo_summary", "repo_tree", "list_repos", "resolve_repos", "context_pack"]);

  const searchCount = events.filter((e) => SEARCH_TOOLS.has(e.toolName)).length;
  const readCount = events.filter((e) => READ_TOOLS.has(e.toolName)).length;
  const repoCount = events.filter((e) => REPO_TOOLS.has(e.toolName)).length;

  const parts: string[] = [];
  if (searchCount > 0) parts.push(`searched ${searchCount} time${searchCount !== 1 ? "s" : ""}`);
  if (readCount > 0) parts.push(`read ${readCount} file${readCount !== 1 ? "s" : ""}`);
  if (repoCount > 0) parts.push(`checked ${repoCount} repo${repoCount !== 1 ? "s" : ""}`);

  return parts.length > 0
    ? parts.join(" · ")
    : `checked ${events.length} source${events.length !== 1 ? "s" : ""}`;
}
